import type { RouteLocationNormalized } from 'vue-router'
import { api } from '~/services/api'
import { useSessionStore } from '~/stores/session'

interface OrgList {
  organizations?: Array<{ id: string }>
}

/**
 * Sanitize a redirect query parameter to prevent open-redirect attacks.
 * Only local paths starting with '/' (or empty) are allowed.
 */
function sanitizeRedirect(redirect: unknown): string {
  if (typeof redirect !== 'string') return '/app'
  const trimmed = redirect.trim()
  if (!trimmed || trimmed.startsWith('/')) return trimmed || '/app'
  // Reject external URLs
  return '/app'
}

/**
 * Navigation guard — replaces the auth.ts + onboarding.ts route middleware.
 *
 * Ordering (preserved from the prior middleware setup):
 *  1. If route requiresAuth: await sessionStore.ensure(); if not authenticated → /auth?redirect=...
 *  2. If route requiresOrganization (ONLY after auth passes): fetch /api/organizations;
 *     if zero orgs → /onboarding
 *
 * /join/:code is requiresAuth only (NOT requiresOrganization) — the route table already encodes
 * this, so the guard handles it automatically by checking requiresOrganization only after auth.
 */
export async function beforeEach(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized
): Promise<string | { path: string; query?: Record<string, string>; replace?: boolean } | undefined> {
  const session = useSessionStore()

  // ── 1. Auth gate ─────────────────────────────────────────────────────────
  if (to.meta.requiresAuth) {
    await session.ensure()

    if (!session.authenticated) {
      const redirect = sanitizeRedirect(to.fullPath)
      return {
        path: '/auth',
        query: { redirect },
        replace: true,
      }
    }
  }

  // ── 2. Organization gate (only after auth passes) ─────────────────────────
  if (to.meta.requiresOrganization) {
    try {
      const res = await api<OrgList>('/api/organizations')
      if (!res?.organizations?.length) {
        return { path: '/onboarding', replace: true }
      }
    } catch {
      // Network/parse failure: don't block navigation — `auth` already gates the
      // unauthenticated case, and an org-scoped API call will still 409 `no_org`
      // if truly orgless.
    }
  }

  // Continue to the route.
  return undefined
}
